/**
 * Global Teen Theme Wrapper
 * Applies the teen's selected theme to the entire app
 * Sets CSS custom properties on :root for components to use
 */

import { useEffect, useState, useCallback } from 'react';
import { TEEN_THEMES } from '../contexts/TeenThemeContext';

const GlobalTeenTheme = ({ children }) => {
  const [isTeenMode, setIsTeenMode] = useState(() => {
    return localStorage.getItem('teen_onboarded') === 'true';
  });

  const applyTheme = useCallback(() => {
    const isOnboarded = localStorage.getItem('teen_onboarded') === 'true';
    
    if (!isOnboarded) {
      setIsTeenMode(false);
      // Remove teen mode attributes
      document.documentElement.removeAttribute('data-teen-mode');
      document.documentElement.removeAttribute('data-teen-theme');
      document.documentElement.classList.remove('teen-mode');
      // Reset body styles to default
      document.body.style.backgroundColor = '';
      document.body.style.color = '';
      return;
    }

    setIsTeenMode(true);

    // Get the selected theme
    const savedTheme = localStorage.getItem('teen_selected_theme');
    const ageGroup = localStorage.getItem('teen_age_bracket');
    
    let theme = null;
    
    if (savedTheme) {
      try {
        theme = JSON.parse(savedTheme);
      } catch (e) {
        console.warn('Failed to parse saved teen theme');
      }
    }
    
    // Fallback to age-based default theme
    if (!theme && ageGroup) {
      const ageToTheme = {
        '12-14': TEEN_THEMES.younger,
        '15-16': TEEN_THEMES.mid,
        '17-18': TEEN_THEMES.older,
      };
      theme = ageToTheme[ageGroup];
    }
    
    if (theme?.colors) {
      // Set CSS variables on document root
      const root = document.documentElement;
      root.style.setProperty('--teen-base', theme.colors.base);
      root.style.setProperty('--teen-surface', theme.colors.surface);
      root.style.setProperty('--teen-primary', theme.colors.primary || theme.colors.accent);
      root.style.setProperty('--teen-accent', theme.colors.accentSecondary || theme.colors.accent);
      root.style.setProperty('--teen-text-primary', theme.colors.textPrimary);
      root.style.setProperty('--teen-text-secondary', theme.colors.textSecondary);
      
      // Set data attributes for CSS targeting
      root.dataset.teenMode = 'true';
      root.dataset.teenTheme = theme.mode || 'dark';
      root.classList.add('teen-mode');
      
      // For dark themes, also set body background
      if (theme.mode === 'dark') {
        document.body.style.backgroundColor = theme.colors.base;
        document.body.style.color = theme.colors.textPrimary;
      }
    }
  }, []);

  // Apply theme on mount
  useEffect(() => {
    applyTheme();
  }, [applyTheme]);

  // Re-check periodically
  useEffect(() => {
    const interval = setInterval(() => {
      const currentOnboarded = localStorage.getItem('teen_onboarded') === 'true';
      if (currentOnboarded !== isTeenMode) {
        applyTheme();
      }
    }, 500);
    
    return () => clearInterval(interval);
  }, [isTeenMode, applyTheme]);

  // Listen for storage changes
  useEffect(() => {
    window.addEventListener('storage', applyTheme);
    return () => window.removeEventListener('storage', applyTheme);
  }, [applyTheme]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.documentElement.removeAttribute('data-teen-mode');
      document.documentElement.removeAttribute('data-teen-theme');
      document.documentElement.classList.remove('teen-mode');
      document.body.style.backgroundColor = '';
      document.body.style.color = '';
    };
  }, []);

  return children;
};

export default GlobalTeenTheme;
